import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { stripStatePrefix } from './utils/tenant';
import Loader from 'wrapper/loader';

// Redux Store and Actions
import { store } from './redux/store';
import { setUserFromToken } from './redux/slices/authSlice';

// Session Management
import sessionManager from './services/session/SessionManager';

// Auth Bootstrap
import AuthBootstrapWrapper from './components/auth/AuthBootstrapWrapper';

// Main App Content
import AppContentNew from './components/AppContentWrapper/AppContentNew';
import PlatformLogin from '@/pages/platform/PlatformLogin';
import PlatformAuthCallback from '@/pages/platform/PlatformAuthCallback';
import OAuthCallback from '@/pages/shared/auth/oauth/OAuthCallback';
import PlatformLayout from './layouts/PlatformLayout';
import PlatformProtectedRoute from './components/auth/PlatformProtectedRoute';

// Platform Pages
import PlatformDashboard from './pages/platform/Dashboard';
import CompanyManagement from './pages/platform/CompanyManagement';
import CompanyInvitations from './pages/platform/CompanyInvitations';
import Analytics from './pages/platform/Analytics';
import Settings from './pages/platform/Settings';

// Shadcn Components
import { Toaster } from '@/components/ui/toaster';

// Styles
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';

// Initialize Sentry for error tracking
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Set document title
document.title = import.meta.env.VITE_APP_TITLE || 'MIRA - Loan Origination System';

const App = () => {
  const [isTokenProcessed, setIsTokenProcessed] = React.useState(false);

  useEffect(() => {
    // Check for token in URL parameters (for cross-subdomain authentication)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const userFromUrl = urlParams.get('user');

    if (tokenFromUrl && userFromUrl) {
      console.log('[App] Token found in URL, storing for subdomain authentication');
      try {
        // Parse user data
        const userData = JSON.parse(decodeURIComponent(userFromUrl));

        // Import tokenManager and store the token
        import('./services/auth/tokenManager').then(({ default: tokenManager }) => {
          tokenManager.setUserData(userData);
          if (!import.meta.env.PROD) {
            console.log('[App] User data stored from URL parameter');
          }

          // Update Redux state
          store.dispatch(setUserFromToken());
          console.log('[App] Redux state updated');

          // Clean up URL to remove sensitive data
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);

          // Mark token as processed
          setIsTokenProcessed(true);
        });
      } catch (error) {
        console.error('[App] Failed to process token from URL:', error);
        setIsTokenProcessed(true);
      }
    } else {
      // No token in URL, proceed normally
      setIsTokenProcessed(true);
    }

    // Initialize SessionManager
    sessionManager.initialize();
    console.log('MIRA App initialized with SessionManager');

    // Set up session expiry handlers
    sessionManager.onSessionWarning = (timeRemaining) => {
      console.log(`Session expiring in ${timeRemaining} seconds`);
      // Could show a modal or notification here
    };

    sessionManager.onSessionExpired = () => {
      console.log('Session expired');
      // Will be handled by apiClient interceptor
    };

    return () => {
      // Cleanup on unmount
      sessionManager.cleanup();
    };
  }, []);

  // Check if we're on a platform route
  // All other routes (including /onboard) are handled by AppContentNew
  const normalizedPathname = stripStatePrefix(window.location.pathname);
  const isPlatformRoute = normalizedPathname.startsWith('/platform');

  // Wait for token processing before rendering anything
  if (!isTokenProcessed) {
    return (
      <Provider store={store}>
        <Loader message="Initializing..." />
      </Provider>
    );
  }

  return (
    <Provider store={store}>
      <AuthBootstrapWrapper store={store}>
        {isPlatformRoute ? (
          <BrowserRouter>
            <Routes>
              {/* Platform Admin Routes - Public */}
              <Route path="/platform/login" element={<PlatformLogin />} />
              <Route path="/platform/auth/callback" element={<PlatformAuthCallback />} />

              {/* Platform Admin Routes - Protected (Authentication Required) */}
              <Route path="/platform/*" element={
                <PlatformProtectedRoute>
                  <PlatformLayout />
                </PlatformProtectedRoute>
              }>
                <Route index element={<PlatformDashboard />} />
                <Route path="dashboard" element={<PlatformDashboard />} />
                <Route path="companies" element={<CompanyManagement />} />
                <Route path="invitations" element={<CompanyInvitations />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="settings" element={<Settings />} />
              </Route>
            </Routes>
          </BrowserRouter>
        ) : (
          <AppContentNew />
        )}
      </AuthBootstrapWrapper>
      {/* Shadcn Toast Notifications */}
      <Toaster />
    </Provider>
  );
};

export default App;
